/**
 * translationTiling.ts — PLAT-10270 (T10, epic PLAT-10209)
 *
 * Pure tiling helpers for the split controls + Backspace recombine: the
 * split-state predicates and the three CLIENT-SIDE transforms — collapse
 * (Undo split), reapply (Redo split), and merge-into-previous (Backspace) —
 * over plain `phrases[]`. The backend splitter (PLAT-10269) is the only
 * server-side op; everything here rebuilds the phrase array locally and the
 * store persists it via the verbatim Save.
 *
 * No React, no MobX, no store imports — a deterministic projection over plain
 * `TranslationPhrase` / `ParentPhrase` / `TranslationSegment` data so it
 * unit-tests in isolation (same idiom as `translationSpanEdit.ts`). The store
 * ops (Atom 2) resolve lead → segment via `getSegments` and compose these.
 *
 * Data-model contract (DESIGN "Data model / state"):
 * - The collapse CACHE is the segment's retired fragments WITHOUT
 *   `supersededBy` — retired-with-`supersededBy` is owned by the override
 *   model (`revertOverrideSegment` keys on it) and is never touched here.
 * - Collapse retires, never deletes; merge replaces without retiring
 *   (`parentPhrases[]` pins reconstruction).
 * - Every transform keeps the segment's active fragments tiling exactly the
 *   parent's `originalIndices` (coverage invariant).
 * - All transforms are immutable: they return a NEW array and never mutate
 *   the input phrases.
 */

import { joinPhraseValues } from '@src/utility/strings';

import {
  phraseLead,
  unionOriginalElementIds,
  unionOriginalIndices,
} from './translationSpanEdit';

import type {
  ParentPhrase,
  TranslationPhrase,
  TranslationSegment,
} from '@src/types.ts/translation';

/**
 * Whether a segment is currently SPLIT — i.e. tiled by more than one active
 * fragment. `getSegments` already returns only active fragments, but the
 * predicate re-filters defensively so a hand-built segment answers the same.
 */
export function isSegmentSplit(segment: TranslationSegment): boolean {
  let activeCount = 0;
  for (const f of segment.fragments) {
    if (f.active !== false) activeCount++;
    if (activeCount > 1) return true;
  }
  return false;
}

/**
 * Whether a collapsed split is CACHED for the segment: some phrase is retired
 * (`active: false`) under this `parentId` with `supersededBy` ABSENT.
 * Override-retired phrases always carry `supersededBy` (and an override's
 * retired originals carry the OLD parentId, not the override's), so they never
 * count. Synthetic `solo:<lead>` segment ids never match by construction — no
 * phrase carries them as `parentId`.
 */
export function hasCachedSplit(
  phrases: readonly TranslationPhrase[],
  segmentId: string,
): boolean {
  return phrases.some(
    p =>
      p.parentId === segmentId &&
      p.active === false &&
      p.supersededBy === undefined,
  );
}

/**
 * Undo split — collapse a split segment back to ONE coarse phrase.
 *
 * Rebuilds `phrases[]`: (a) DROPS any previous cache generation for this
 * segment (retired, non-superseded, `parentId === parent.id`) so a re-split
 * never leaves two cache generations; (b) retires the current active
 * fragments in place (`active: false`, `parentId` kept, `supersededBy` NOT
 * set — they become the new redo cache); (c) inserts one active coarse phrase
 * at the first fragment's position, carrying the parent's `originalIndices`
 * and `translatedValue` (the pre-fragmenting text), the fragments' snapshots
 * joined with ' ' (same convention as `revertOverrideSegment`), the first
 * fragment's `handler`, and `isUserEdited` when ANY fragment was user-edited.
 * `originalTranslatedValue` stays ABSENT — never fabricate a machine original
 * (the gate-on-presence convention protects Revert).
 *
 * Returns `null` (no-op) when the segment has ≤1 active fragment or its
 * fragments aren't found in `phrases`.
 */
export function collapseSegment(
  phrases: readonly TranslationPhrase[],
  parent: ParentPhrase,
  fragments: readonly TranslationPhrase[],
): TranslationPhrase[] | null {
  const active = fragments
    .filter(f => f.active !== false)
    .sort((a, b) => phraseLead(a) - phraseLead(b));
  if (active.length <= 1) return null;

  const coarse: TranslationPhrase = {
    originalIndices: [...parent.originalIndices],
    // Stable-ID identity (PLAT-10268): the coarse phrase must carry the union of
    // its fragments' element ids, or the UUID-keyed coverage/badge lookups
    // (`entryForPhrase` on `originalElementIds`) find nothing overlapping it and
    // the coverage badge — the only entry point to the split controls —
    // disappears after Undo split. Mirrors the `originalIndices` line.
    originalElementIds: unionOriginalElementIds(active),
    translatedValue: parent.translatedValue,
    isUserEdited: active.some(f => f.isUserEdited),
    originalValueSnapshot: active
      .map(f => f.originalValueSnapshot ?? '')
      .join(' '),
    handler: active[0].handler,
    parentId: parent.id,
  };

  const activeSet = new Set(active);
  const next: TranslationPhrase[] = [];
  let inserted = false;
  for (const p of phrases) {
    // (a) Drop the previous cache generation for this segment. The retired
    // copies created below are new objects, so they never hit this check.
    if (
      p.parentId === parent.id &&
      p.active === false &&
      p.supersededBy === undefined
    ) {
      continue;
    }
    if (activeSet.has(p)) {
      // (c) The coarse phrase takes the first fragment's position …
      if (!inserted) {
        next.push(coarse);
        inserted = true;
      }
      // … (b) and the fragments stay behind it, retired (the redo cache).
      next.push({ ...p, active: false });
      continue;
    }
    next.push(p);
  }
  if (!inserted) return null;
  return next;
}

/**
 * Redo split — reapply the cached tiling written by `collapseSegment`.
 *
 * Removes the segment's current active phrase(s) and reactivates the cached
 * retired fragments (`active: true`; no other field changed) — restoring the
 * exact pre-collapse tiling. Note: an edit made on the collapsed coarse
 * phrase is discarded by redo (accepted v1 rule — the changelog records both
 * states).
 *
 * Returns `null` when the segment has no cache or is currently split
 * (mirrors the popover's `canRedoSplit = hasCachedSplit && !isSplit`).
 */
export function reapplySegmentSplit(
  phrases: readonly TranslationPhrase[],
  segmentId: string,
): TranslationPhrase[] | null {
  if (!hasCachedSplit(phrases, segmentId)) return null;
  const activeCount = phrases.filter(
    p => p.parentId === segmentId && p.active !== false,
  ).length;
  if (activeCount > 1) return null;

  return phrases
    .filter(p => !(p.parentId === segmentId && p.active !== false))
    .map(p =>
      p.parentId === segmentId &&
      p.active === false &&
      p.supersededBy === undefined
        ? { ...p, active: true }
        : p,
    );
}

/**
 * Backspace recombine — merge the active fragment with the given `lead` (its
 * smallest source `initial_index`, the value the DOM stamps on
 * `data-initial-index`) into the fragment BEFORE it in the same segment.
 *
 * Replaces the previous+current fragments with ONE phrase at the previous
 * fragment's position: `originalIndices` = sorted union,
 * `translatedValue` = `joinPhraseValues` of the two values (the
 * punctuation-aware PLAT-10453 join), snapshots joined with ' ', the shared
 * `parentId`, `isUserEdited` when either was edited, the previous fragment's
 * `handler`, and `originalTranslatedValue` ABSENT. NO retirement on merge —
 * `parentPhrases[]` pins reconstruction; only collapse writes the cache.
 *
 * Returns `null` when the lead isn't found among the segment's fragments,
 * when it is the segment's FIRST fragment (never merge across a segment
 * boundary — Jira AC 4), or when the two fragments don't share a non-null
 * `parentId` (a solo fragment is its own segment and can never merge).
 *
 * `editedBy` (optional) is stamped onto the merged phrase when provided — the
 * store passes the current reviewer (same attribution convention as
 * `createOverrideSegment`); a pure caller may omit it.
 */
export function mergeFragmentIntoPrevious(
  phrases: readonly TranslationPhrase[],
  segment: TranslationSegment,
  lead: number,
  editedBy?: TranslationPhrase['editedBy'],
): TranslationPhrase[] | null {
  const idx = segment.fragments.findIndex(f => phraseLead(f) === lead);
  if (idx <= 0) return null; // not found, or the segment's first fragment
  const prev = segment.fragments[idx - 1];
  const curr = segment.fragments[idx];
  if (prev.parentId === undefined || prev.parentId !== curr.parentId) {
    return null;
  }
  if (!phrases.includes(prev) || !phrases.includes(curr)) return null;

  const merged: TranslationPhrase = {
    originalIndices: unionOriginalIndices([prev, curr]),
    // Stable-ID identity (PLAT-10268): carry the union of both fragments'
    // element ids, else the merged phrase's coverage/badge lookup (UUID-keyed)
    // finds nothing overlapping it — same badge-drop class as `collapseSegment`.
    originalElementIds: unionOriginalElementIds([prev, curr]),
    translatedValue: joinPhraseValues([
      prev.translatedValue,
      curr.translatedValue,
    ]),
    isUserEdited: prev.isUserEdited || curr.isUserEdited,
    originalValueSnapshot: [prev, curr]
      .map(p => p.originalValueSnapshot ?? '')
      .join(' '),
    handler: prev.handler,
    parentId: prev.parentId,
    ...(editedBy ? { editedBy } : {}),
  };

  const next: TranslationPhrase[] = [];
  for (const p of phrases) {
    if (p === prev) {
      next.push(merged); // the merged phrase takes the previous's position
      continue;
    }
    if (p === curr) continue; // absorbed into `merged`
    next.push(p);
  }
  return next;
}
